import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as authApi from '../lib/authApi';
import type { AuthSession, AuthUser } from '../lib/authApi';
import { registerSessionRefresh, getTokenExpiryMs } from '../lib/api';
import { clearBrandCache } from '../lib/brandCache';
import { queryClient } from '../lib/queryClient';
import { isTestModeEnabled, setTestModeEnabled } from '../lib/testMode';
import { buildDemoSession, isDemoCredentials, isDemoToken } from '../lib/demoAuth';
import { useCompanyStore } from './useCompanyStore';

/**
 * Forget everything the previous session cached before a new one starts.
 *
 * The React Query cache is the big one: it is a module singleton that outlives
 * login and logout, and every list the sidebar draws (projects, clients, the
 * firm switcher, the brand) has a stale time of minutes. Going from the demo
 * account to a real one left the demo's lists on screen until they expired —
 * "Firmio Demo" in the switcher, the demo projects under Proiecte — because
 * Test Mode was switched off through this store, which the provider's
 * cache-clearing toggle never saw.
 *
 * The persisted firm selection and the cached brand only need resetting when
 * the *kind* of account changes: a real user logging back in keeps their firm.
 */
function resetSessionCaches(nextIsDemo: boolean): void {
  queryClient.clear();
  if (isTestModeEnabled() !== nextIsDemo) {
    useCompanyStore.getState().setActiveCompanyId(null);
    clearBrandCache();
  }
}

// Proactive session keep-alive (CLE-442). The reactive 401 recovery (CLE-439)
// only kicks in after a request has already failed, and it does not cover raw
// `fetch` paths (e.g. the daily-summary PDF export). Users on a short access
// token still saw intermittent 401s and had to refresh the page. To prevent the
// token from ever expiring while the tab is open, we schedule a background
// refresh shortly BEFORE the access token's `exp`, and reschedule after every
// successful refresh. This turns the reactive path into a rare fallback.

// Refresh this many ms before the token actually expires, so the new token is
// in place well ahead of any in-flight request.
const REFRESH_LEAD_MS = 60_000;
// Never schedule sooner than this, so a very short-lived (or already near-expiry)
// token cannot spin a tight refresh loop.
const MIN_REFRESH_DELAY_MS = 5_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

// (Re)arm the background refresh for the current access token. No-ops for the
// demo session (synthetic token) or any token without a decodable `exp`.
function scheduleProactiveRefresh(accessToken: string | null): void {
  clearRefreshTimer();
  if (!accessToken || isDemoToken(accessToken)) return;
  const expMs = getTokenExpiryMs(accessToken);
  if (expMs === null) return;
  const delay = Math.max(expMs - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
  refreshTimer = setTimeout(() => {
    void useAuthStore.getState().refreshSession();
  }, delay);
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  // True until we have finished the initial token/session check on app load.
  initializing: boolean;

  login: (email: string, password: string) => Promise<void>;
  // Enter Test Mode via the dedicated demo account — no backend call.
  loginDemo: () => Promise<void>;
  // Resolves with verificationRequired=true when the account was created but
  // the user must click the emailed verification link before logging in.
  register: (
    email: string,
    password: string,
    name: string,
  ) => Promise<{ verificationRequired: boolean }>;
  // Adopt a session obtained outside login/register (verify-email, accept-invite).
  setSession: (session: AuthSession) => void;
  // Patch the signed-in user in place (profile edits such as the avatar).
  updateUser: (patch: Partial<AuthUser>) => void;
  logout: () => Promise<void>;
  // Exchange the stored refresh token for a fresh token pair. Returns the new
  // access token, or null if refresh failed (session is then cleared).
  refreshSession: () => Promise<string | null>;
  // Run once on app start to validate/restore the persisted session.
  initialize: () => Promise<void>;

  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      initializing: true,

      login: async (email, password) => {
        // Dedicated demo account (CLE-251): drop straight into Test Mode with a
        // synthetic admin session. Never calls the backend.
        if (isDemoCredentials(email, password)) {
          const res = buildDemoSession();
          resetSessionCaches(true);
          setTestModeEnabled(true);
          set({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken });
          return;
        }
        // Real login: ensure Test Mode is OFF so the user always lands on real
        // data, then authenticate against the backend.
        resetSessionCaches(false);
        setTestModeEnabled(false);
        const res = await authApi.login({ email, password });
        set({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken });
        scheduleProactiveRefresh(res.accessToken);
      },

      loginDemo: async () => {
        const res = buildDemoSession();
        resetSessionCaches(true);
        setTestModeEnabled(true);
        set({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken });
      },

      register: async (email, password, name) => {
        setTestModeEnabled(false);
        const res = await authApi.register({ email, password, name });
        if (authApi.isPendingVerification(res)) {
          // Account created but unverified: no tokens yet — the user finishes
          // the flow via the emailed link (VerifyEmailPage adopts the session).
          return { verificationRequired: true };
        }
        set({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken });
        scheduleProactiveRefresh(res.accessToken);
        return { verificationRequired: false };
      },

      updateUser: (patch) => {
        set((state) => ({ user: state.user ? { ...state.user, ...patch } : state.user }));
      },

      setSession: (session) => {
        resetSessionCaches(false);
        setTestModeEnabled(false);
        set({
          user: session.user,
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
        });
        scheduleProactiveRefresh(session.accessToken);
      },

      logout: async () => {
        const { accessToken } = get();
        // Leaving the demo session also leaves Test Mode (CLE-251), so the next
        // login lands cleanly on the real login screen / real data. The cache
        // goes with it, whichever kind of account this was.
        resetSessionCaches(false);
        setTestModeEnabled(false);
        if (accessToken && !isDemoToken(accessToken)) {
          // Best-effort server notification; ignore failures. Skipped for the
          // demo session, whose token is synthetic (no backend logout).
          await authApi.logout(accessToken).catch(() => undefined);
        }
        clearRefreshTimer();
        // Drop the cached brand so the next account doesn't briefly render the
        // previous company's name/logo in the sidebar.
        clearBrandCache();
        set({ user: null, accessToken: null, refreshToken: null });
      },

      refreshSession: async () => {
        const { refreshToken } = get();
        // Demo session: synthetic token, nothing to refresh — keep it alive.
        if (isDemoToken(refreshToken)) {
          const res = buildDemoSession();
          setTestModeEnabled(true);
          set({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken });
          return res.accessToken;
        }
        if (!refreshToken) {
          clearRefreshTimer();
          set({ user: null, accessToken: null, refreshToken: null });
          return null;
        }
        try {
          const res = await authApi.refresh(refreshToken);
          set({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken });
          // Arm the next proactive refresh against the freshly issued token so
          // the session stays alive indefinitely while the tab is open.
          scheduleProactiveRefresh(res.accessToken);
          return res.accessToken;
        } catch {
          clearRefreshTimer();
          set({ user: null, accessToken: null, refreshToken: null });
          return null;
        }
      },

      initialize: async () => {
        const { accessToken, refreshToken } = get();
        if (!accessToken && !refreshToken) {
          set({ initializing: false });
          return;
        }
        // Restore a persisted demo session without any backend round-trip, and
        // make sure Test Mode is back ON so the simulated dataset is served.
        if (isDemoToken(accessToken) || isDemoToken(refreshToken)) {
          const res = buildDemoSession();
          setTestModeEnabled(true);
          set({ user: res.user, accessToken: res.accessToken, initializing: false });
          return;
        }
        // Try the current access token; on failure, fall back to refresh.
        if (accessToken) {
          try {
            const user = await authApi.fetchMe(accessToken);
            set({ user, initializing: false });
            // Keep the restored session alive: schedule a refresh before this
            // (still valid) access token expires (CLE-442).
            scheduleProactiveRefresh(accessToken);
            return;
          } catch {
            // fall through to refresh
          }
        }
        await get().refreshSession();
        set({ initializing: false });
      },

      isAuthenticated: () => Boolean(get().accessToken && get().user),
    }),
    {
      name: 'ccc-auth',
      // Only persist the durable session fields, not transient flags.
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);

// Let the HTTP layer recover expired sessions transparently (CLE-439): on a 401,
// apiFetch refreshes the token and replays the request instead of crashing the
// page. Registered here (not imported inside api.ts) to avoid a circular import.
registerSessionRefresh(() => useAuthStore.getState().refreshSession());
